import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, Grid, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useApi } from '../../../hooks/useApi';
import { isRowClosed, rowStaleness, rowWaitDays, STALE_THRESHOLD_DAYS } from '../qbrUtils';

function statusColor(status) {
  switch (status) {
    case 'completed': return 'success';
    case 'in progress':
    case 'scheduled':
    case 'open':
    case 'held':
      return 'info';
    default: return 'default';
  }
}

function Detail() {
  const { id } = useParams();
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const [pResp, cResp, oResp] = await Promise.all([
        jApi.request('GET', `cma/qbrprogram?id=${encodeURIComponent(id)}`),
        jApi.request('GET', `cma/qbrcycle?qbrprogram_id=${encodeURIComponent(id)}`),
        jApi.request('GET', `cma/qbropportunity?qbrprogram_id=${encodeURIComponent(id)}`),
      ]);
      setProgram((Array.isArray(pResp.data) ? pResp.data : [])[0] || null);

      const cl = Array.isArray(cResp.data) ? cResp.data : [];
      cl.sort((a, b) => (b.meeting_date || '').localeCompare(a.meeting_date || ''));
      setCycles(cl);

      const ol = Array.isArray(oResp.data) ? oResp.data : [];
      // Open opportunities first, then closed ones; within each, newest first.
      ol.sort((a, b) => {
        const aClosed = !!a.closed_date;
        const bClosed = !!b.closed_date;
        if (aClosed !== bClosed) return aClosed ? 1 : -1;
        return (b.created || 0) - (a.created || 0);
      });
      setOpportunities(ol);
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load program');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [id]);

  // Cross-cycle open audit-issue and action-item rows, flattened with a
  // back-reference to the cycle they came from. Sorted oldest cycle first
  // so the staleness ordering matches "what's been waiting longest".
  // Stale-first sort: longest-waiting at the top so the vCIO sees the items
  // most likely needing a nudge before they scroll.
  const sortStale = (a, b) => rowWaitDays(b) - rowWaitDays(a);

  const openAuditIssues = useMemo(() => {
    const out = [];
    for (const c of cycles) {
      for (const row of (c.audit_issues || [])) {
        if (!row || isRowClosed(row)) continue;
        out.push({
          ...row,
          qbr_cycle:    c.qbr_cycle,
          qbrcycle_id:  c.id,
          meeting_date: c.meeting_date,
        });
      }
    }
    out.sort(sortStale);
    return out;
  }, [cycles]);

  const openActionItems = useMemo(() => {
    const out = [];
    for (const c of cycles) {
      for (const row of (c.action_items || [])) {
        if (!row || isRowClosed(row)) continue;
        out.push({
          ...row,
          qbr_cycle:    c.qbr_cycle,
          qbrcycle_id:  c.id,
          meeting_date: c.meeting_date,
        });
      }
    }
    out.sort(sortStale);
    return out;
  }, [cycles]);

  const openOpportunities = useMemo(
    () => opportunities.filter(o => !o.closed_date),
    [opportunities]
  );

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/qbr" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>QBR</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{program?.customer || id}</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  {program?.customer || (isLoading ? 'Loading…' : 'Not found')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {program ? `${cycles.length} cycle${cycles.length === 1 ? '' : 's'} · Autotask #${program.autotask_id}` : ''}
                </Typography>
              </Box>
              <IconButton size="small" onClick={fetchData} disabled={isLoading}><RefreshIcon/></IconButton>
            </Stack>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {errorMessage && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        )}

        {!isLoading && !program && !errorMessage && (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">No QBR program found for this id.</Typography>
          </Paper>
        )}

        {program && (
          <>
            {/* Profile */}
            <Paper sx={{ p: 2, mt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="overline" color="text.secondary">Account</Typography>
                  <Typography variant="body1"><code>{program.account}</code></Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="overline" color="text.secondary">Account Manager</Typography>
                  <Typography variant="body1">
                    {program.account_manager || '—'}
                    {program.account_manager_email && (
                      <>{' · '}<a href={`mailto:${program.account_manager_email}`}>{program.account_manager_email}</a></>
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="overline" color="text.secondary">vCIO</Typography>
                  <Typography variant="body1">
                    {program.vcio || '—'}
                    {program.vcio_email && (
                      <>{' · '}<a href={`mailto:${program.vcio_email}`}>{program.vcio_email}</a></>
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="overline" color="text.secondary">Target Date</Typography>
                  <Typography variant="body1">{program.target_date || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="overline" color="text.secondary">Cadence</Typography>
                  <Typography variant="body1">{program.cadence_months || 3} months</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="overline" color="text.secondary">Status</Typography>
                  <Typography variant="body1">
                    <Chip size="small" label={program.active ? 'Active' : 'Inactive'} color={program.active ? 'success' : 'default'}/>
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="overline" color="text.secondary">Primary Contact</Typography>
                  <Typography variant="body1">
                    {program.primary_contact_name || '—'}
                    {program.primary_contact_email && (
                      <>{' · '}<a href={`mailto:${program.primary_contact_email}`}>{program.primary_contact_email}</a></>
                    )}
                    {program.primary_contact_phone && (
                      <>{' · '}<a href={`tel:${program.primary_contact_phone}`}>{program.primary_contact_phone}</a></>
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Outstanding — cross-cycle "what still needs attention" view.
                Audit issues + action items are flattened from every cycle's
                embedded arrays; opportunities come from qbropportunity.
                All three are filtered to OPEN only and sorted oldest first. */}
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
                  Outstanding
                </Typography>
                <Chip size="small"
                  label={`${openAuditIssues.length + openActionItems.length + openOpportunities.length} open total`}
                  color={(openAuditIssues.length + openActionItems.length + openOpportunities.length) > 0 ? 'warning' : 'success'}
                  variant={(openAuditIssues.length + openActionItems.length + openOpportunities.length) > 0 ? 'filled' : 'outlined'}/>
              </Stack>

              {/* Open audit issues */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                  AUDIT ISSUES
                </Typography>
                <Chip size="small" label={openAuditIssues.length}
                  color={openAuditIssues.length > 0 ? 'warning' : 'default'}
                  variant={openAuditIssues.length > 0 ? 'filled' : 'outlined'}/>
              </Stack>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 100 }}>Cycle</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: 160 }}>Ticket #</TableCell>
                      <TableCell sx={{ width: 160 }}>Status</TableCell>
                      <TableCell align="right" sx={{ width: 110 }}>Age</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openAuditIssues.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                        No open audit issues.
                      </TableCell></TableRow>
                    )}
                    {openAuditIssues.map((r, i) => {
                      const s = rowStaleness(r);
                      const days = rowWaitDays(r);
                      return (
                      <TableRow key={`${r.qbrcycle_id}-ai-${i}`} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/qbr/cycle/${r.qbrcycle_id}`)}>
                        <TableCell><code>{r.qbr_cycle}</code></TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell>{r.ticket || '—'}</TableCell>
                        <TableCell>{r.status || '—'}</TableCell>
                        <TableCell align="right">
                          {days > 0
                            ? <Chip size="small"
                                label={`${days}d`}
                                color={s.stale ? 'error' : 'default'}
                                variant={s.stale ? 'filled' : 'outlined'}/>
                            : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Open action items */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                  ACTION ITEMS
                </Typography>
                <Chip size="small" label={openActionItems.length}
                  color={openActionItems.length > 0 ? 'warning' : 'default'}
                  variant={openActionItems.length > 0 ? 'filled' : 'outlined'}/>
              </Stack>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 100 }}>Cycle</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: 160 }}>Ticket #</TableCell>
                      <TableCell sx={{ width: 160 }}>Status</TableCell>
                      <TableCell align="right" sx={{ width: 110 }}>Age</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openActionItems.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                        No open action items.
                      </TableCell></TableRow>
                    )}
                    {openActionItems.map((r, i) => {
                      const s = rowStaleness(r);
                      const days = rowWaitDays(r);
                      return (
                      <TableRow key={`${r.qbrcycle_id}-act-${i}`} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/qbr/cycle/${r.qbrcycle_id}`)}>
                        <TableCell><code>{r.qbr_cycle}</code></TableCell>
                        <TableCell>{r.description}</TableCell>
                        <TableCell>{r.ticket || '—'}</TableCell>
                        <TableCell>{r.status || '—'}</TableCell>
                        <TableCell align="right">
                          {days > 0
                            ? <Chip size="small"
                                label={`${days}d`}
                                color={s.stale ? 'error' : 'default'}
                                variant={s.stale ? 'filled' : 'outlined'}/>
                            : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Open opportunities */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                  OPPORTUNITIES
                </Typography>
                <Chip size="small" label={openOpportunities.length}
                  color={openOpportunities.length > 0 ? 'info' : 'default'}
                  variant={openOpportunities.length > 0 ? 'filled' : 'outlined'}/>
              </Stack>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 100 }}>Cycle</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: 140 }}>Opportunity #</TableCell>
                      <TableCell sx={{ width: 140 }}>Stage</TableCell>
                      <TableCell sx={{ width: 140 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openOpportunities.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                        No open opportunities.
                      </TableCell></TableRow>
                    )}
                    {openOpportunities.map(o => (
                      <TableRow key={o.id} hover sx={{ cursor: o.originating_qbrcycle_id ? 'pointer' : 'default' }}
                        onClick={() => o.originating_qbrcycle_id && navigate(`/qbr/cycle/${o.originating_qbrcycle_id}`)}>
                        <TableCell><code>{o.originating_qbr_cycle || '—'}</code></TableCell>
                        <TableCell>{o.description}</TableCell>
                        <TableCell>{o.opportunity || '—'}</TableCell>
                        <TableCell>{o.stage || '—'}</TableCell>
                        <TableCell>{o.status || 'open'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* QBR history */}
            <Box sx={{ mt: 3, pb: 4 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.5, color: theme.palette.secondary.main }}>
                QBR History
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cycle</TableCell>
                      <TableCell>Meeting Date</TableCell>
                      <TableCell>Audit Notify</TableCell>
                      <TableCell>Audit Status</TableCell>
                      <TableCell>Meeting Status</TableCell>
                      <TableCell>QBR Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cycles.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                        No cycles on file yet.
                      </TableCell></TableRow>
                    )}
                    {cycles.map(c => (
                      <TableRow key={c.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/qbr/cycle/${c.id}`)}>
                        <TableCell><code>{c.qbr_cycle}</code></TableCell>
                        <TableCell>{c.meeting_date}</TableCell>
                        <TableCell>{c.audit_notification_date}</TableCell>
                        <TableCell><Chip size="small" label={c.audit_status}   color={statusColor(c.audit_status)}/></TableCell>
                        <TableCell><Chip size="small" label={c.meeting_status} color={statusColor(c.meeting_status)}/></TableCell>
                        <TableCell><Chip size="small" label={c.qbr_status}     color={statusColor(c.qbr_status)}/></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

          </>
        )}
      </Container>
    </Box>
  );
}

export default Detail;
